import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import {
  yieldToMainThread,
  getViewParameterValue,
  ResizeNotifier,
} from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';
const SECTION_ID_MAP = {
  'quick-add-component': 'section-rendering-product-card',
  'swatches-variant-picker-component': 'section-rendering-product-card',
  'featured-product-information': undefined,
};
/**
 * Mirror the picked variant on the URL via `history.replaceState`, respecting
 * `loadsNewProduct` (different `connectedProductUrl`) and `isOnProductPage`.
 *
 * Behavior:
  * - On the product template page, sets/removes the `variant` query param.
  * - When the option selects a different connected product, rewrites `pathname`
    to `newUrl`.
  * - No-op when the resulting URL is identical to the current location.
  * - Schedules the history write on the next macrotask via `yieldToMainThread()`.
  *
  * @param {object} options
  * @param {boolean} options.isOnProductPage
  * @param {boolean} options.loadsNewProduct
  * @param {string} options.variantId
  * @param {string} options.newUrl
  * @returns {void}
  */ function syncVariantUrl({
  isOnProductPage: isOnProductPage,
  loadsNewProduct: loadsNewProduct,
  variantId: variantId,
  newUrl: newUrl,
}) {
  const url = new URL(window.location.href);
  if (isOnProductPage) {
    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }
  }
  if (loadsNewProduct) {
    url.pathname = newUrl;
  }
  if (url.href !== window.location.href) {
    yieldToMainThread().then(() => {
      history.replaceState({}, '', url.toString());
    });
  }
}
/**
 * `<variant-picker>` web component.
 *
 * Owns the radio/select inputs used to choose a product variant, fetches the
 * matching section HTML, and dispatches `ProductSelectEvent` so the rest of
 * the page (product form, quick-add modal, etc.) can react.
 *
 * @element variant-picker
 * @fires ProductSelectEvent#productSelect
 */ export default class VariantPicker extends Component {
  #pendingRequestUrl;
  #abortController;
  #checkedIndices = [];
  #radios = [];
  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());
  #rebuildFieldsetCache() {
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = this.refs.fieldsets || [];
    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);
      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.dataset.currentChecked === 'true',
      );
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    this.#rebuildFieldsetCache();
    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }
  /**
   * Handle a `change` event on one of the variant inputs and trigger the
   * corresponding section fetch.
   *
   * @param {Event} event
   * @returns {void}
   */ variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;
    if (!selectedOption) return;
    this.updateSelectedOption(event.target);
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(
      this.closest('featured-product-information'),
    );
    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;
    const {
      optionValueId: optionValueId = '',
      variantId: variantId = '',
      connectedProductUrl: connectedProductUrl = '',
    } = selectedOption.dataset;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector: morphElementSelector,
      detail: {
        optionValueId: optionValueId,
        variantId: variantId,
        connectedProductUrl: connectedProductUrl,
      },
    });
    syncVariantUrl({
      isOnProductPage: isOnProductPage,
      loadsNewProduct: loadsNewProduct,
      variantId: variantId,
      newUrl: newUrl,
    });
  }
  #resolveInputIndices(input) {
    const fieldsets = this.refs.fieldsets || [];
    const fieldset = input.closest('fieldset');
    if (!fieldset) return null;
    const fieldsetIndex = fieldsets.indexOf(fieldset);
    if (fieldsetIndex === -1) return null;
    const sameNameInputs = Array.from(
      fieldset.querySelectorAll(`input[name="${CSS.escape(input.name)}"]`),
    );
    if (sameNameInputs.length === 0) return null;
    const inputIndex = sameNameInputs.indexOf(input);
    if (inputIndex === -1) return null;
    return { fieldsetIndex: fieldsetIndex, inputIndex: inputIndex };
  }
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = this.refs.fieldsets || [];
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];
    if (!radios || !checkedIndices || !fieldset) return null;
    const [currentIndex, previousIndex] = checkedIndices;
    return {
      fieldset: fieldset,
      currentIndex: currentIndex,
      previousIndex: previousIndex,
      currentWidth:
        currentIndex !== undefined
          ? radios[currentIndex]?.parentElement?.offsetWidth
          : undefined,
      previousWidth:
        previousIndex !== undefined
          ? radios[previousIndex]?.parentElement?.offsetWidth
          : undefined,
    };
  }
  #applyFieldsetMeasurements({
    fieldset: fieldset,
    currentWidth: currentWidth,
    previousWidth: previousWidth,
    currentIndex: currentIndex,
    previousIndex: previousIndex,
  }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }
    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;
    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`,
      );
      if (!targetElement) {
        console.warn(
          `[variant-picker] Option value "${target}" not found; keeping current selection.`,
        );
        return;
      }
      target = targetElement;
    }
    if (target instanceof HTMLInputElement) {
      let fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      let inputIndex = Number.parseInt(target.dataset.inputIndex || '');
      if (Number.isNaN(fieldsetIndex) || Number.isNaN(inputIndex)) {
        const derived = this.#resolveInputIndices(target);
        if (derived) {
          fieldsetIndex = derived.fieldsetIndex;
          inputIndex = derived.inputIndex;
        }
      }
      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = this.refs.fieldsets || [];
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];
        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;
          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);
          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }
          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue,
      );
      if (!newSelectedOption) throw new Error('Option not found');
      for (const option of target.options) {
        option.removeAttribute('selected');
      }
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }
  /**
   * Builds the section-rendering request URL for the given selected option,
   * honoring the pending product URL, the current view parameter and the
   * `option_values` of the source (`product-card` or picker-internal).
   *
   * @param {HTMLElement} selectedOption
   * @param {string|null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string}
   */ buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = [],
  ) {
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }
    const getSectionId = (key) =>
      key === 'featured-product-information'
        ? this.closest('featured-product-information')?.id
        : SECTION_ID_MAP[key];
    const closestSectionId = Object.keys(SECTION_ID_MAP).find((sectionId) =>
      this.closest(sectionId),
    );
    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${getSectionId(closestSectionId)}&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }
  /**
   * Fetches the section HTML for the request URL and resolves the deferred
   * `ProductSelectEvent` promise — dispatching the event to subscribers —
   * optionally morphing the requested element when `morphElementSelector` is
   * given.
   *
   * @param {string} requestUrl
   * @param {{morphElementSelector?: string, detail?: {optionValueId?: string, variantId?: string, connectedProductUrl?: string}}} [options]
   * @returns {void}
   */ fetchUpdatedSection(
    requestUrl,
    { morphElementSelector: morphElementSelector, detail: detail = {} } = {},
  ) {
    const {
      optionValueId: optionValueId = '',
      variantId: variantId,
      connectedProductUrl: connectedProductUrl = '',
    } = detail;
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();
    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions: selectedOptions,
        detail: {
          optionValueId: optionValueId,
          variantId: variantId,
          connectedProductUrl: connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      }),
    );
    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => {
        if (!response.ok)
          throw new Error(`variant picker failed: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
        let newProduct;
        const variantPickerJsonScript = html.querySelector(
          `${this.tagName.toLowerCase()} script[type="application/json"]`,
        );
        const textContent = variantPickerJsonScript?.textContent;
        if (!textContent) {
          this.#resolveNullVariant(deferredEventPromise, html, newProduct);
          return;
        }
        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList: overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList &&
            overflowList.getAttribute('disabled') === 'true';
          newProduct = this.updateVariantPicker(html);
          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }
        if (this.selectedOptionId) {
          const variantData = this.#safeJsonParse(
            textContent,
            'Failed to parse variant picker JSON:',
          );
          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();
            let selectedVariant = null;
            if (productViewAttr) {
              selectedVariant =
                this.#safeJsonParse(
                  productViewAttr,
                  'Failed to parse view-event-payload:',
                )?.product?.selectedVariant ?? null;
            }
            deferredEventPromise.resolve({
              variant: selectedVariant,
              detail: {
                html: html,
                productId: this.dataset.productId ?? '',
                newProduct: newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });
            return;
          }
        }
        this.#resolveNullVariant(deferredEventPromise, html, newProduct);
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }
  /**
   * Resolves the deferred `ProductSelectEvent` promise with no variant and no
   * resource, mirroring the section HTML to the subscriber.
   *
   * @param {ReturnType<typeof ProductSelectEvent.createPromise>} deferredEventPromise
   * @param {Document} html
   * @param {{ id: string; url: string } | undefined} newProduct
   * @returns {void}
   */ #resolveNullVariant(deferredEventPromise, html, newProduct) {
    deferredEventPromise.resolve({
      variant: null,
      detail: {
        html: html,
        productId: this.dataset.productId ?? '',
        ...(newProduct !== undefined && { newProduct: newProduct }),
        sourceId: this.selectedOptionId,
        resource: null,
      },
    });
  }
  /**
   * Parses a JSON string, warning with the given label and returning `null`
   * when parsing fails.
   *
   * @param {string} text
   * @param {string} warnLabel
   * @returns {unknown | null}
   */ #safeJsonParse(text, warnLabel) {
    try {
      return JSON.parse(text);
    } catch (e) {
      console.warn(warnLabel, e);
      return null;
    }
  }
  updateVariantPicker(newHtml) {
    let newProduct;
    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase(),
    );
    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;
      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl };
      }
      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }
    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#rebuildFieldsetCache();
    this.updateVariantPickerCss();
    return newProduct;
  }
  updateVariantPickerCss() {
    const fieldsets = this.refs.fieldsets || [];
    const measurements = fieldsets
      .map((_, index) => this.#getFieldsetMeasurements(index))
      .filter((m) => m !== null);
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);
    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }
    morph(element, newElement);
  }
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) {
      throw new Error('No new main source found');
    }
    morph(main, newMain);
  }
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked',
    );
    if (!(
      selectedOption instanceof HTMLInputElement ||
      selectedOption instanceof HTMLOptionElement
    )) {
      return undefined;
    }
    return selectedOption;
  }
  getAllSelectedOptions() {
    const options = [];
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({
          name: selected.dataset.optionName,
          value: selected.value,
        });
      }
    }
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }
    return options;
  }
  get selectedOptionId() {
    const { selectedOption: selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId: optionValueId } = selectedOption.dataset;
    return optionValueId || null;
  }
  get selectedOptionsValues() {
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked'),
    );
    return selectedOptions
      .map((option) => option.dataset.optionValueId)
      .filter((id) => typeof id === 'string' && id.length > 0);
  }
}
if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
